/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState } from 'react';

import type { DispatchAgentOption } from '@/Client/State/useDispatchAction';
import { styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily, styleguideLetterSpacing, styleguideSpacing } from '@kaiizen/kai-shiki/core';
import { MODAL_BASE_PRIMARY_BUTTON_STYLE, MODAL_BASE_SECONDARY_BUTTON_STYLE, ModalBase } from './ModalBase';

export const DISPATCH_TITLE = 'Dispatch Scope';
export const DISPATCH_CONFIRM_LABEL = 'Dispatch';
export const DISPATCH_INSTRUCTIONS = 'Field agents in this market';
export const CHANGE_BUTTON_TEXT = 'Change';

const META_STYLE = css`
  label: DISPATCH_META_STYLE;
  border: 1px solid ${styleguideColors.stroke};
  padding: 14px 16px;
  margin-bottom: ${styleguideSpacing.s5}px;
`;

const META_ADDRESS_STYLE = css`
  label: DISPATCH_META_ADDRESS_STYLE;
  font-family: ${styleguideFontFamily.display};
  font-size: ${pxToRem(styleguideFontSize.bodyDefault)};
  font-weight: ${styleguideFontWeight.bold};
  color: ${styleguideColors.ink};
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const META_ID_STYLE = css`
  label: DISPATCH_META_ID_STYLE;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(10)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${styleguideColors.midtone};
  margin-top: 5px;
`;

const PICK_HEADER_STYLE = css`
  label: DISPATCH_PICK_HEADER_STYLE;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(9)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: ${styleguideLetterSpacing.monoCaption};
  text-transform: uppercase;
  color: ${styleguideColors.midtone};
  margin-bottom: ${styleguideSpacing.s2}px;
`;

const LIST_STYLE = css`
  label: DISPATCH_AGENT_LIST_STYLE;
  border: 1px solid ${styleguideColors.stroke};
  max-height: 268px;
  overflow-y: auto;
`;

const ROW_STYLE = (selected: boolean) =>
  css`
  label: DISPATCH_AGENT_ROW_STYLE;
  display: flex;
  align-items: center;
  gap: ${styleguideSpacing.s3}px;
  width: 100%;
  padding: 11px ${styleguideSpacing.s4}px;
  background: ${selected ? styleguideColors.accent : styleguideColors.white};
  border: 0;
  border-bottom: 1px solid ${styleguideColors.stroke};
  cursor: pointer;
  text-align: left;

  &:last-child { border-bottom: 0; }
  &:hover { background: ${selected ? styleguideColors.accent : styleguideColors.sunken}; }
`;

const AVATAR_STYLE = css`
  label: DISPATCH_AGENT_AVATAR_STYLE;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  background: ${styleguideColors.ink};
  color: ${styleguideColors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(10)};
  font-weight: ${styleguideFontWeight.bold};
`;

const LABEL_COLUMN_STYLE = css`
  label: DISPATCH_AGENT_LABEL_COLUMN_STYLE;
  flex: 1;
  min-width: 0;
`;

const AGENT_NAME_STYLE = css`
  label: DISPATCH_AGENT_NAME_STYLE;
  font-family: ${styleguideFontFamily.display};
  font-size: ${pxToRem(styleguideFontSize.bodyMedium)};
  font-weight: ${styleguideFontWeight.bold};
  color: ${styleguideColors.ink};
  display: block;
`;

const AGENT_ROLE_STYLE = css`
  label: DISPATCH_AGENT_ROLE_STYLE;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(9)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${styleguideColors.midtone};
  margin-top: 7px;
  display: block;
`;

const DEFAULT_BADGE_STYLE = css`
  label: DISPATCH_DEFAULT_BADGE_STYLE;
  flex-shrink: 0;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(8.5)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${styleguideColors.midtone};
  border: 1px solid ${styleguideColors.stroke};
  padding: 2px 6px;
  line-height: 1.4;
  background: ${styleguideColors.white};
`;

const CHECKBOX_STYLE = (selected: boolean) =>
  css`
  label: DISPATCH_AGENT_CHECKBOX_STYLE;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border: 1.5px solid ${selected ? styleguideColors.ink : styleguideColors.stroke};
  background: ${selected ? styleguideColors.ink : styleguideColors.white};
  color: ${selected ? styleguideColors.white : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ASSIGN_ROW_STYLE = css`
  label: DISPATCH_ASSIGN_ROW_STYLE;
  display: flex;
  align-items: center;
  gap: ${styleguideSpacing.s3}px;
  border: 1px solid ${styleguideColors.stroke};
  padding: 12px 14px;
`;

const ASSIGN_ROW_LIST_STYLE = css`
  label: DISPATCH_ASSIGN_ROW_LIST_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${styleguideSpacing.s2}px;
`;

const CHANGE_BUTTON_STYLE = css`
  label: DISPATCH_CHANGE_BUTTON_STYLE;
  flex-shrink: 0;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(9.5)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${styleguideColors.ink};
  background: none;
  border: 1px solid ${styleguideColors.ink};
  padding: 6px 12px;
  cursor: pointer;

  &:hover { background: ${styleguideColors.ink}; color: ${styleguideColors.white}; }
`;

const getInitials = (label: string) => label.split(' ').map(part => part[0]).slice(0, 2).join('').toUpperCase();

const CheckIcon = () => (
  <svg viewBox='0 0 12 12' width={10} height={10} stroke='currentColor' strokeWidth={2} fill='none'>
    <path d='M2.5 6.5L5 9L9.5 3.5' />
  </svg>
);

const SendIcon = () => (
  <svg viewBox='0 0 24 24' width={22} height={22} stroke='currentColor' strokeWidth={1.8} fill='none'>
    <path d='M21 3L10 14M21 3l-7 18-4-7-7-4 18-7z' />
  </svg>
);

export const DispatchModal = ({
  agentOptions,
  defaultAgentIds,
  projectAddress,
  projectLabel,
  busy = false,
  onConfirm,
  onCancel,
}: {
  agentOptions: DispatchAgentOption[];
  /** Whoever's already assigned when the modal opens - previewed on a confirm screen with a Change button, instead of dropping straight into the full picker. */
  defaultAgentIds: string[];
  projectAddress?: string;
  projectLabel?: string;
  busy?: boolean;
  onConfirm: (selectedAgentIds: string[]) => void;
  onCancel: () => void;
}) => {
  const [mode, setMode] = useState<'confirm' | 'pick'>(defaultAgentIds.length > 0 ? 'confirm' : 'pick');
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>(defaultAgentIds);
  const toggle = (id: string) => setSelectedAgentIds(current => current.includes(id) ? current.filter(existing => existing !== id) : [...current, id]);

  const defaultAgents = agentOptions.filter(agent => defaultAgentIds.includes(agent.value));
  // Falls back to pick mode if agentOptions hasn't loaded yet on first render
  // (useUsers() resolves async) and can't yet resolve defaultAgentIds to real
  // agent rows - otherwise confirm mode would render an empty box with no
  // "Change" button to escape it from.
  const effectiveMode = mode === 'confirm' && defaultAgents.length === 0 ? 'pick' : mode;

  return (
    <ModalBase
      title={DISPATCH_TITLE}
      centered
      icon={<SendIcon />}
      onOverlayClick={onCancel}
      actions={
        <>
          <button type='button' css={MODAL_BASE_SECONDARY_BUTTON_STYLE} disabled={busy} onClick={onCancel}>Cancel</button>
          <button
            type='button'
            css={MODAL_BASE_PRIMARY_BUTTON_STYLE}
            disabled={busy || selectedAgentIds.length === 0}
            onClick={() => onConfirm(selectedAgentIds)}
          >
            {DISPATCH_CONFIRM_LABEL}
          </button>
        </>
      }
    >
      {(projectAddress || projectLabel)
        ? (
          <div css={META_STYLE}>
            {projectAddress ? <div css={META_ADDRESS_STYLE}>{projectAddress}</div> : null}
            {projectLabel ? <div css={META_ID_STYLE}>{projectLabel}</div> : null}
          </div>
        )
        : null}

      {effectiveMode === 'confirm'
        ? (
          <div css={ASSIGN_ROW_LIST_STYLE}>
            {defaultAgents.map(agent => (
              <div key={agent.value} css={ASSIGN_ROW_STYLE}>
                <span css={AVATAR_STYLE}>{getInitials(agent.label)}</span>
                <span css={LABEL_COLUMN_STYLE}>
                  <span css={AGENT_NAME_STYLE}>{agent.label}</span>
                  <span css={AGENT_ROLE_STYLE}>{agent.roleLabel}</span>
                </span>
                <button type='button' css={CHANGE_BUTTON_STYLE} onClick={() => setMode('pick')}>{CHANGE_BUTTON_TEXT}</button>
              </div>
            ))}
          </div>
        )
        : (
          <>
            <p css={PICK_HEADER_STYLE}>{DISPATCH_INSTRUCTIONS}</p>
            <div css={LIST_STYLE}>
              {agentOptions.map(agent => {
                const selected = selectedAgentIds.includes(agent.value);
                const isDefault = defaultAgentIds.includes(agent.value);
                return (
                  <button key={agent.value} type='button' css={ROW_STYLE(selected)} onClick={() => toggle(agent.value)}>
                    <span css={AVATAR_STYLE}>{getInitials(agent.label)}</span>
                    <span css={LABEL_COLUMN_STYLE}>
                      <span css={AGENT_NAME_STYLE}>{agent.label}</span>
                      <span css={AGENT_ROLE_STYLE}>{agent.roleLabel}</span>
                    </span>
                    {isDefault ? <span css={DEFAULT_BADGE_STYLE}>Default</span> : null}
                    <span css={CHECKBOX_STYLE(selected)}>
                      <CheckIcon />
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
    </ModalBase>
  );
};
